import { Calendar } from "@iventis/calendar";
import { Button, FormControl } from "@mui/material";
import { ErrorLarge } from "@iventis/components/src/error-large";
import { customMediaQueryMax, customMediaQueryMin, media, screenSizeBreakpoints, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { inputHeight, tableToolBarHeight } from "@iventis/styles/src/atomic-rules";
import { ConciseSelector } from "@iventis/components";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { NestedDropDownComponent } from "@iventis/components/src/nested-dropdown";
import { ScheduleViewBySelector } from "./view-by-selector";
import { DaySelector } from "./day-selector";

const createBlueStyleRules = (textColour: string, borderColour: string) => `
    .MuiSelect-icon {
        color: ${textColour};
    }

    .MuiSelect-outlined.MuiSelect-outlined {
        border: 1px solid ${borderColour};
        color: ${textColour};
        height: ${inputHeight};
        box-sizing: border-box;
    }

    &.MuiCheckbox-root, &.MuiCheckbox-root.Mui-checked {
        color: ${textColour};
    }
`;

export const StyledGroupedRow = styled.div`
    width: calc(100% - 20px);
    padding: 10px;
    flex-wrap: wrap;

    .MuiSelect-select {
        font-weight: bold;

        ${customMediaQueryMin(screenSizeBreakpoints.large)} {
            margin-left: 5px;
        }
    }

    ${customMediaQueryMin(screenSizeBreakpoints.large)} {
        display: flex;
        align-items: center;
    }

    .margin-bottom-stacked {
        ${customMediaQueryMax(screenSizeBreakpoints.large)} {
            margin-bottom: 10px;
        }
    }

    .stretch-stacked {
        ${customMediaQueryMax(screenSizeBreakpoints.large)} {
            min-width: 100%;
        }
    }

    .grouped-selectors-stacked {
        ${customMediaQueryMax(screenSizeBreakpoints.large)} {
            display: flex;

            > :nth-child(1) {
                flex: 1 1 50%;
            }

            > :nth-child(2) {
                flex: 1 1 50%;
                margin-left: 10px;
            }
        }
    }

    .right-hand-selectors {
        display: flex;

        button {
            margin-left: 5px;
        }

        ${customMediaQueryMax(screenSizeBreakpoints.extraLarge)} {
            display: none;
        }

        ${customMediaQueryMin(screenSizeBreakpoints.extraLarge)} {
            margin-left: auto;
        }
    }
`;

export const StyledContentHeaders = styled.div`
    display: none;

    ${customMediaQueryMax(screenSizeBreakpoints.extraLarge)} {
        display: flex;
    }

    width: calc(100% - 10px);
    margin-right: 10px;
    justify-content: flex-end;
    border-top: 1px solid ${({ theme }: { theme: Theme }) => theme.secondaryColors.warm};
    align-items: center;
    height: 46px;

    > * {
        margin-left: 10px;
    }
`;

export const StyledCalendarHeaderButtonLarge = styled(Button)`
    display: flex;
    height: 43px;
    border: 1px solid ${({ theme }: { theme: Theme }) => theme.primaryColors.altFocusBorder};
    color: ${({ theme }: { theme: Theme }) => theme.primaryColors.altFocusText};

    :hover {
        border: 1px solid ${({ theme }: { theme: Theme }) => theme.primaryColors.altFocusBorder}; // repeated here because we must override mui theme default on hover
        background-color: ${({ theme }: { theme: Theme }) => theme.shades.four};
    }
`;

export const StyledCalendarHeaderButtonSmall = styled(Button)`
    display: flex;
    height: 100%;
    border: 0;
    color: ${({ theme }: { theme: Theme }) => theme.typographyColors.core};

    &:hover {
        border: 0;
        background-color: transparent;
    }
`;

// It is literally impossible to refactor the following repeated rule
export const StyledSelector = styled(ConciseSelector)`
    ${({ theme }: { theme: Theme }) => createBlueStyleRules(theme.primaryColors.altFocusText, theme.primaryColors.altFocusBorder)}
`;
export const StyledViewBy = styled(ScheduleViewBySelector)`
    ${({ theme }: { theme: Theme }) => createBlueStyleRules(theme.primaryColors.altFocusText, theme.primaryColors.altFocusBorder)}
`;
export const StyledDaySelector = styled(DaySelector)`
    ${({ theme }: { theme: Theme }) => createBlueStyleRules(theme.primaryColors.altFocusText, theme.primaryColors.altFocusBorder)}
`;
export const StyledScheduleVersionSelector = styled(NestedDropDownComponent)`
    ${({ theme }: { theme: Theme }) => createBlueStyleRules(theme.primaryColors.altFocusText, theme.primaryColors.altFocusBorder)}
    width: 100%;
    font-weight: bold;
`;

export const StyledDataFieldSelector = styled(FormControl)`
    ${({ theme }: { theme: Theme }) => createBlueStyleRules(theme.primaryColors.altFocusText, theme.primaryColors.altFocusBorder)}
    span {
        text-overflow: ellipsis;
        max-width: 100px;
        overflow: hidden;
        white-space: nowrap;

        ${media.medium} {
            max-width: 150px;
        }
        ${media.large} {
            max-width: 200px;
        }
    }
`;

export const StyledMainSection = styled.section<{ sidebarWidth: number; showSidebar: boolean }>`
    height: 100%;
    width: ${({ sidebarWidth }) => `calc(100% - ${sidebarWidth}px)`};
    display: ${({ showSidebar }) => `${showSidebar ? "none" : "flex"}`};
    flex-direction: column;
    align-items: stretch;
    justify-content: stretch;
    ${media.small} {
        display: flex;
    }

    .add-session-btn {
        position: absolute;
        bottom: 20px;
        right: 27px;
    }
    .key-selector {
        position: absolute;
        bottom: 70px;
        right: 20px;
    }
`;

export const StyledPage = styled.div`
    display: flex;
    justify-content: stretch;
    align-items: stretch;
    height: 100%;
    overflow: hidden;
`;

export const StyledMainContentContainer = styled.div`
    position: relative;
    flex-grow: 1;
    display: flex;
    justify-content: stretch;
    align-items: stretch;
    overflow: hidden;
`;

export const HeadSection = styled.div<{ showSideBar: boolean }>``;

export const StyledSchedule = styled(Calendar)`
    flex: 1 1 100%;
`;

export const StyledTableWrapper = styled.section`
    flex: 1 1;
    height: 100%;
    padding-left: 10px;
    padding-right: 10px;
    display: grid;
    grid-template-rows: ${tableToolBarHeight} calc(100% - ${tableToolBarHeight});

    .ag-grid-iventis {
        grid-row: 2 / 3;
        width: 100%;
    }
`;

// Show which representation we can toggle to
export const representationToggleIcons = new Map<boolean, IconName>([
    [false, "table"],
    [true, "calendar"],
]);

export const SidebarHeader = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 50px;
    background-color: ${({ theme }: { theme: Theme }) => theme.primaryColors.altFocusText};
    color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};

    ${media.small} {
        display: none;
    }
`;

export const CloseIcon = styled.div`
    display: flex;
    flex-grow: 1;
    justify-content: flex-end;
`;

export const HeaderContent = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 90%;
    height: 50px;
    .header-title {
        margin-left: 10px;
    }
`;

export const StyledErrorLarge = styled(ErrorLarge)`
    width: 100%;
    height: 100%;
`;
